/* eslint-disable -- Documents is a disabled WIP feature (Onyx vault sync); kept out of the dock until file sync is stable */
import { useState, useCallback } from 'react';
import {
  FolderOpen, Plus, FileText, RefreshCw, Folder, Edit2, Trash2,
  ChevronRight, ChevronDown, FolderPlus, X, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import type { NostrEvent } from '@nostrify/nostrify';
const uuidv4 = () => crypto.randomUUID();

// ─── Types ───────────────────────────────────────────────────────────────────

interface VaultFileEntry {
  eventId: string;
  d: string;
  path: string;
  checksum: string;
  version: number;
  modified: number;
}

interface DeletedEntry {
  path: string;
  deletedAt: number;
  lastEventId?: string;
}

interface VaultIndex {
  name: string;
  description?: string;
  created: number;
  files: VaultFileEntry[];
  deleted?: DeletedEntry[];
}

interface DocumentFile {
  path: string;
  content: string;
  version: number;
  modified: number;
  eventId?: string;
  d?: string;
}

interface TreeNode {
  name: string;
  path: string;
  isFolder: boolean;
  children: TreeNode[];
  file?: DocumentFile;
}

// ─── Modal types ─────────────────────────────────────────────────────────────

type ModalState =
  | { type: 'none' }
  | { type: 'delete'; node: TreeNode }
  | { type: 'rename'; node: TreeNode; value: string }
  | { type: 'createFolder'; parentPath: string; value: string }
  | { type: 'createNote'; parentPath: string; value: string };

// ─── Utilities ───────────────────────────────────────────────────────────────

function getName(path: string): string {
  return path.split('/').filter(Boolean).pop() || path;
}

function getParentPath(path: string): string {
  const parts = path.split('/').filter(Boolean);
  if (parts.length <= 1) return '/';
  return '/' + parts.slice(0, -1).join('/');
}

/** Hide internal `.keep` placeholder files in folders */
function isKeepFile(path: string): boolean {
  return path.endsWith('/.keep');
}

function buildTree(files: DocumentFile[], deletedPaths: Set<string>): TreeNode {
  const root: TreeNode = { name: 'Root', path: '/', isFolder: true, children: [] };

  // Filter out deleted paths and .keep files
  const visibleFiles = files.filter(
    f => !deletedPaths.has(f.path) && !isKeepFile(f.path)
  );

  // Deduplicate by path
  const uniqueFiles = Array.from(new Map(visibleFiles.map(f => [f.path, f])).values());

  uniqueFiles.forEach(file => {
    const parts = file.path.split('/').filter(Boolean);
    let current = root;

    parts.forEach((part, idx) => {
      const isLast = idx === parts.length - 1;
      const currentPath = '/' + parts.slice(0, idx + 1).join('/');

      if (isLast) {
        if (!current.children.some(c => c.path === currentPath)) {
          current.children.push({ name: part, path: currentPath, isFolder: false, children: [], file });
        }
      } else {
        let folder = current.children.find(c => c.isFolder && c.path === currentPath);
        if (!folder) {
          folder = { name: part, path: currentPath, isFolder: true, children: [] };
          current.children.push(folder);
        }
        current = folder;
      }
    });
  });

  return root;
}

async function calcChecksum(content: string): Promise<string> {
  const data = new TextEncoder().encode(content);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DocumentsApp() {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedVault, setSelectedVault] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<DocumentFile | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['/']));
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  // Stable query key — never changes after mount for the same user
  const VAULTS_KEY = ['documents-vaults', user?.pubkey] as const;

  // ─── Vault query ───────────────────────────────────────────────────────────

  const { data: vaults = [] } = useQuery({
    queryKey: VAULTS_KEY,
    queryFn: async ({ signal }) => {
      if (!user) return [];
      const events = await nostr.query(
        [{ kinds: [30801], authors: [user.pubkey], limit: 50 }],
        { signal }
      );

      // Keep only the newest event per d-tag
      const latestByD = new Map<string, NostrEvent>();
      events.forEach(ev => {
        const d = ev.tags.find(t => t[0] === 'd')?.[1];
        if (d && (!latestByD.has(d) || ev.created_at > latestByD.get(d)!.created_at))
          latestByD.set(d, ev);
      });

      const result: Array<{ id: string; name: string; index: VaultIndex; event: NostrEvent }> = [];
      for (const ev of latestByD.values()) {
        try {
          const d = ev.tags.find(t => t[0] === 'd')?.[1]!;
          const res = user.signer.nip44!.decrypt(user.pubkey, ev.content);
          const content = res instanceof Promise ? await res : res;
          const index: VaultIndex = JSON.parse(content);
          result.push({ id: d, name: index.name, index, event: ev });
        } catch {
          // Skip malformed vaults
        }
      }
      return result;
    },
    enabled: !!user,
  });

  const currentVault = vaults.find(v => v.id === selectedVault) ?? null;

  // ─── Optimistic cache patch ─────────────────────────────────────────────────
  // Cancel in-flight refetch FIRST, then write, so relay data can't race us.

  const patchVaultCache = useCallback(async (vaultId: string, newIndex: VaultIndex) => {
    await queryClient.cancelQueries({ queryKey: VAULTS_KEY });
    queryClient.setQueryData(
      VAULTS_KEY,
      (old: typeof vaults | undefined) => {
        if (!old) return old;
        return old.map(v => v.id === vaultId ? { ...v, index: newIndex } : v);
      }
    );
  }, [queryClient, VAULTS_KEY]);

  // ─── Derived file list ──────────────────────────────────────────────────────

  const deletedPaths = new Set<string>(
    currentVault?.index.deleted?.map(d => d.path) ?? []
  );

  const allFiles: DocumentFile[] = currentVault
    ? Array.from(
        new Map(
          currentVault.index.files
            .filter(f => !deletedPaths.has(f.path))
            .map(f => [f.path, {
              path: f.path,
              content: '',
              version: f.version,
              modified: f.modified,
              d: f.d,
              eventId: f.eventId,
            }])
        ).values()
      )
    : [];

  const tree = buildTree(allFiles, deletedPaths);

  // ─── Tree expansion ─────────────────────────────────────────────────────────

  const toggleExpand = (path: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  };

  // ─── Open file ──────────────────────────────────────────────────────────────

  const openFile = async (file: DocumentFile) => {
    if (!user) return;
    setSelectedFile(file);
    setEditorContent('Loading…');

    if (!file.d) {
      setEditorContent(file.content || '');
      return;
    }

    try {
      const events = await nostr.query(
        [{ kinds: [30800], authors: [user.pubkey], '#d': [file.d], limit: 1 }]
      );
      if (events.length > 0) {
        const res = user.signer.nip44!.decrypt(user.pubkey, events[0].content);
        const decrypted = res instanceof Promise ? await res : res;
        const payload = JSON.parse(decrypted);
        const body = payload.content || '';
        setEditorContent(body);
        setSelectedFile(prev => prev ? { ...prev, content: body } : prev);
      } else {
        setEditorContent(file.content || '');
      }
    } catch {
      setEditorContent(file.content || '');
    }
  };

  // ─── Save (internal) ────────────────────────────────────────────────────────

  const saveInternal = async (file: DocumentFile, content: string): Promise<void> => {
    const vaultId = selectedVault;
    if (!user || !currentVault || !vaultId) return;
    setIsSaving(true);
    try {
      const d = file.d || uuidv4();
      const now = Math.floor(Date.now() / 1000);
      const payload = {
        path: file.path,
        content,
        checksum: await calcChecksum(content),
        version: (file.version || 0) + 1,
        modified: now,
        previousEventId: file.eventId || null,
        contentType: 'text/markdown',
      };

      const encRes = user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(payload));
      const encrypted = encRes instanceof Promise ? await encRes : encRes;
      await publish({ kind: 30800, content: encrypted, tags: [['d', d], ['encrypted', 'nip44']], created_at: now });

      // Build updated file list — replace or append
      const freshFiles = [...currentVault.index.files];
      const idx = freshFiles.findIndex(f => f.d === d || f.path === file.path);
      const entry: VaultFileEntry = {
        eventId: '',
        d,
        path: file.path,
        checksum: payload.checksum,
        version: payload.version,
        modified: now,
      };
      if (idx >= 0) freshFiles[idx] = entry; else freshFiles.push(entry);

      const newIndex: VaultIndex = { ...currentVault.index, files: freshFiles };
      const idxEnc = user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(newIndex));
      const idxEncrypted = idxEnc instanceof Promise ? await idxEnc : idxEnc;
      await publish({ kind: 30801, content: idxEncrypted, tags: [['d', vaultId], ['encrypted', 'nip44']], created_at: now });

      setSelectedFile({ ...file, content, d, version: payload.version, modified: now });
      await patchVaultCache(vaultId, newIndex);
    } catch {
      toast({ title: 'Save failed', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const save = () => selectedFile && saveInternal(selectedFile, editorContent);

  // ─── Create note ────────────────────────────────────────────────────────────

  const createNote = () => {
    if (!selectedVault) return;
    setModal({ type: 'createNote', parentPath: '/', value: '' });
  };

  const confirmCreateNote = async (name: string) => {
    if (!selectedVault || !name.trim()) return;
    const safeName = name.trim().endsWith('.md') ? name.trim() : `${name.trim()}.md`;
    const path = `/${safeName}`;
    const newDoc: DocumentFile = { path, content: '# ' + safeName.replace('.md', ''), version: 1, modified: Math.floor(Date.now() / 1000) };
    setModal({ type: 'none' });
    await saveInternal(newDoc, newDoc.content);
    setSelectedFile(newDoc);
    setEditorContent(newDoc.content);
  };

  // ─── Create folder ──────────────────────────────────────────────────────────

  const createFolder = () => {
    if (!selectedVault) return;
    setModal({ type: 'createFolder', parentPath: '/', value: '' });
  };

  const confirmCreateFolder = async (name: string) => {
    if (!name.trim() || !currentVault) return;
    const folderName = name.trim();
    const keepPath = `/${folderName}/.keep`;
    const placeholder: DocumentFile = { path: keepPath, content: '', version: 1, modified: Math.floor(Date.now() / 1000), d: uuidv4() };
    setModal({ type: 'none' });
    await saveInternal(placeholder, '');
    // Expand the new folder
    setExpanded(prev => new Set([...prev, `/${folderName}`]));
    toast({ title: 'Folder created' });
  };

  // ─── Rename ─────────────────────────────────────────────────────────────────

  const startRename = (node: TreeNode) => {
    setModal({ type: 'rename', node, value: node.name.replace(/\.md$/i, '') });
  };

  const confirmRename = async (node: TreeNode, newName: string) => {
    const vaultId = selectedVault;
    if (!newName.trim() || !currentVault || !user || !vaultId) return;
    setModal({ type: 'none' });

    const oldPath = node.path;
    const parent = oldPath.substring(0, oldPath.lastIndexOf('/')) || '/';
    // Preserve .md extension for notes
    let finalName = newName.trim();
    if (!node.isFolder && oldPath.endsWith('.md') && !finalName.endsWith('.md')) {
      finalName += '.md';
    }
    const newPath = parent === '/' ? `/${finalName}` : `${parent}/${finalName}`;
    if (newPath === oldPath) return;

    setIsSaving(true);
    try {
      // Collect all files under this path (for folder rename)
      const toRename = allFiles.filter(f =>
        f.path === oldPath || f.path.startsWith(oldPath + '/')
      );
      const freshFiles = [...currentVault.index.files];

      for (const f of toRename) {
        const rel = f.path.slice(oldPath.length);
        const target = newPath + rel;
        const d = f.d || uuidv4();
        const now = Math.floor(Date.now() / 1000);

        // Fetch latest content for files we're renaming
        let content = f.content || '';
        if (f.d) {
          try {
            const evts = await nostr.query([{ kinds: [30800], authors: [user.pubkey], '#d': [f.d], limit: 1 }]);
            if (evts.length > 0) {
              const res = user.signer.nip44!.decrypt(user.pubkey, evts[0].content);
              const dec = res instanceof Promise ? await res : res;
              content = JSON.parse(dec).content || '';
            }
          } catch { /* use cached */ }
        }

        const payload = {
          path: target, content, checksum: await calcChecksum(content),
          version: (f.version || 0) + 1, modified: now,
          previousEventId: f.eventId || null, contentType: 'text/markdown',
        };
        const enc = user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(payload));
        const encrypted = enc instanceof Promise ? await enc : enc;
        await publish({ kind: 30800, content: encrypted, tags: [['d', d], ['encrypted', 'nip44']], created_at: now });

        const idx = freshFiles.findIndex(x => x.d === d || x.path === f.path);
        if (idx >= 0) freshFiles[idx] = { ...freshFiles[idx], path: target, d };
      }

      const updatedIndex: VaultIndex = { ...currentVault.index, files: freshFiles };
      const idxEnc = user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(updatedIndex));
      const idxEncrypted = idxEnc instanceof Promise ? await idxEnc : idxEnc;
      await publish({
        kind: 30801,
        content: idxEncrypted,
        tags: [['d', vaultId], ['encrypted', 'nip44']],
        created_at: Math.floor(Date.now() / 1000),
      });

      await patchVaultCache(vaultId, updatedIndex);
      toast({ title: 'Renamed' });

      if (selectedFile?.path.startsWith(oldPath)) {
        setSelectedFile(null);
        setEditorContent('');
      }
    } catch {
      toast({ title: 'Rename failed', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────

  const startDelete = (node: TreeNode) => {
    setModal({ type: 'delete', node });
  };

  const confirmDelete = async (node: TreeNode) => {
    const vaultId = selectedVault;
    if (!currentVault || !user || !vaultId) return;
    setModal({ type: 'none' });
    setIsSaving(true);
    try {
      const now = Math.floor(Date.now() / 1000);

      // All index entries that belong to this node (file or folder subtree)
      const removedEntries = currentVault.index.files.filter(f =>
        f.path === node.path || f.path.startsWith(node.path + '/')
      );

      // Keep everything else
      const freshFiles = currentVault.index.files.filter(f =>
        f.path !== node.path && !f.path.startsWith(node.path + '/')
      );

      // Append tombstones for removed entries
      const newTombstones: DeletedEntry[] = removedEntries.map(f => ({
        path: f.path,
        deletedAt: now,
        lastEventId: f.eventId || undefined,
      }));
      const freshDeleted = [...(currentVault.index.deleted ?? []), ...newTombstones];

      const updatedIndex: VaultIndex = {
        ...currentVault.index,
        files: freshFiles,
        deleted: freshDeleted,
      };

      // Publish updated vault index with delete applied
      const enc = user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(updatedIndex));
      const encrypted = enc instanceof Promise ? await enc : enc;
      await publish({
        kind: 30801,
        content: encrypted,
        tags: [['d', vaultId], ['encrypted', 'nip44']],
        created_at: now,
      });

      // Clear editor if we deleted the open file
      if (selectedFile && (
        selectedFile.path === node.path ||
        selectedFile.path.startsWith(node.path + '/')
      )) {
        setSelectedFile(null);
        setEditorContent('');
      }

      // Optimistic update — this is the critical path. Cancel any pending
      // query refetches first so they don't overwrite our deletion.
      await patchVaultCache(vaultId, updatedIndex);
      toast({ title: 'Deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', description: String(e), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Move document ──────────────────────────────────────────────────────────

  const [moveTarget, setMoveTarget] = useState<DocumentFile | null>(null);

  const moveDocument = async (file: DocumentFile, newFolderPath: string) => {
    if (!currentVault || !user || !selectedVault) return;

    const fileName = getName(file.path);
    const newPath = newFolderPath === '/' ? `/${fileName}` : `${newFolderPath}/${fileName}`;
    if (newPath === file.path) { setMoveTarget(null); return; }

    setIsSaving(true);
    try {
      const d = file.d || uuidv4();
      const now = Math.floor(Date.now() / 1000);

      let latestContent = file.content;
      if (file.d) {
        try {
          const events = await nostr.query([{ kinds: [30800], authors: [user.pubkey], '#d': [file.d], limit: 1 }]);
          if (events.length > 0) {
            const res = user.signer.nip44!.decrypt(user.pubkey, events[0].content);
            const dec = res instanceof Promise ? await res : res;
            latestContent = JSON.parse(dec).content || '';
          }
        } catch { /* use cached */ }
      }

      const payload = {
        path: newPath,
        content: latestContent,
        checksum: await calcChecksum(latestContent),
        version: (file.version || 0) + 1,
        modified: now,
        previousEventId: file.eventId || null,
        contentType: 'text/markdown',
      };

      const encRes = user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(payload));
      const encrypted = encRes instanceof Promise ? await encRes : encRes;
      await publish({ kind: 30800, content: encrypted, tags: [['d', d], ['encrypted', 'nip44']], created_at: now });

      const freshFiles = currentVault.index.files.filter(
        f => f.path !== file.path && f.d !== file.d
      );
      freshFiles.push({ eventId: '', d, path: newPath, checksum: payload.checksum, version: payload.version, modified: now });

      const updatedIndex: VaultIndex = { ...currentVault.index, files: freshFiles };
      const indexEnc = user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(updatedIndex));
      const indexEncrypted = indexEnc instanceof Promise ? await indexEnc : indexEnc;
      await publish({ kind: 30801, content: indexEncrypted, tags: [['d', selectedVault], ['encrypted', 'nip44']], created_at: now });

      await patchVaultCache(selectedVault, updatedIndex);
      toast({ title: `Moved to ${newFolderPath}` });
      setMoveTarget(null);

      if (selectedFile?.path === file.path) {
        setSelectedFile({ ...file, path: newPath, content: latestContent });
        setEditorContent(latestContent);
      }
    } catch (e) {
      toast({ title: 'Move failed', description: String(e), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const allFolders = currentVault
    ? ['/', ...Array.from(new Set(
        allFiles
          .filter(f => !isKeepFile(f.path))
          .map(f => getParentPath(f.path))
          .filter(p => p !== '/')
      )).sort()]
    : [];

  // ─── Tree renderer ──────────────────────────────────────────────────────────

  const renderNode = (node: TreeNode, depth = 0) => {
    if (!node) return null;
    const isExpanded = expanded.has(node.path);
    const isSelected = selectedFile?.path === node.path;

    return (
      <div key={node.path} style={{ paddingLeft: depth * 12 }}>
        <div
          className={`group flex items-center gap-1.5 px-2 py-1 text-sm rounded hover:bg-accent cursor-pointer ${isSelected ? 'bg-accent' : ''}`}
          onClick={() => {
            if (node.isFolder) toggleExpand(node.path);
            else if (node.file) openFile(node.file);
          }}
        >
          {node.isFolder ? (
            <button
              className="p-0.5 shrink-0"
              onClick={e => { e.stopPropagation(); toggleExpand(node.path); }}
            >
              {isExpanded
                ? <ChevronDown className="w-3 h-3" />
                : <ChevronRight className="w-3 h-3" />}
            </button>
          ) : (
            <span className="w-4 shrink-0" />
          )}

          {node.isFolder
            ? <Folder className="w-4 h-4 shrink-0 text-muted-foreground" />
            : <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />}

          <span className="flex-1 truncate">{node.name || 'Unnamed'}</span>

          {/* Action buttons — visible on hover */}
          <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 shrink-0">
            {!node.isFolder && node.file && (
              <Button
                size="icon" variant="ghost" className="h-5 w-5"
                title="Move"
                onClick={e => { e.stopPropagation(); setMoveTarget(node.file!); }}
              >
                <span className="text-[10px]">↕</span>
              </Button>
            )}
            <Button
              size="icon" variant="ghost" className="h-5 w-5"
              title="Rename"
              onClick={e => { e.stopPropagation(); startRename(node); }}
            >
              <Edit2 className="w-3 h-3" />
            </Button>
            <Button
              size="icon" variant="ghost" className="h-5 w-5 hover:text-destructive"
              title="Delete"
              onClick={e => { e.stopPropagation(); startDelete(node); }}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {node.isFolder && isExpanded && node.children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  };

  // ─── Guard: not logged in ───────────────────────────────────────────────────

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
        Login required to use Documents
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full overflow-hidden bg-background">

      {/* Sidebar */}
      <div className="w-72 border-r flex flex-col shrink-0">

        {/* Header */}
        <div className="p-3 border-b flex items-center justify-between">
          <div className="font-semibold text-sm flex items-center gap-1.5">
            <FolderOpen className="w-4 h-4" /> Documents
          </div>
          <Button
            variant="ghost" size="icon"
            title="Refresh"
            onClick={() => {
              setIsSyncing(true);
              queryClient.invalidateQueries({ queryKey: VAULTS_KEY });
              setTimeout(() => setIsSyncing(false), 800);
            }}
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Vault list */}
        <div className="p-2 border-b">
          {Array.from(new Map(vaults.map(v => [v.id, v])).values()).map(v => (
            <div
              key={v.id}
              onClick={() => setSelectedVault(v.id)}
              className={`px-3 py-1.5 text-sm rounded cursor-pointer truncate ${selectedVault === v.id ? 'bg-accent font-medium' : 'hover:bg-accent/50'}`}
            >
              {v.name}
            </div>
          ))}
          {vaults.length === 0 && (
            <div className="text-xs text-muted-foreground px-3 py-2">
              No vaults found. Sync with Onyx to get started.
            </div>
          )}
        </div>

        {/* Toolbar */}
        {selectedVault && (
          <div className="p-2 flex gap-1.5 border-b">
            <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={createNote}>
              <Plus className="w-3 h-3 mr-1" /> Note
            </Button>
            <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={createFolder}>
              <FolderPlus className="w-3 h-3 mr-1" /> Folder
            </Button>
          </div>
        )}

        {/* File tree */}
        <div className="flex-1 overflow-auto py-1 text-sm">
          {selectedVault && tree.children.length > 0 && tree.children.map(node => renderNode(node))}
          {selectedVault && tree.children.length === 0 && (
            <div className="p-4 text-xs text-muted-foreground text-center">
              Empty vault — create a note to get started
            </div>
          )}
          {!selectedVault && vaults.length > 0 && (
            <div className="p-4 text-xs text-muted-foreground text-center">
              Select a vault above
            </div>
          )}
        </div>
      </div>

      {/* Editor pane */}
      <div className="flex-1 flex flex-col min-w-0">
        {selectedFile ? (
          <>
            <div className="h-11 border-b px-4 flex items-center justify-between bg-muted/30 shrink-0">
              <div className="font-medium text-sm truncate pr-4 text-muted-foreground">
                {selectedFile.path}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm" variant="outline"
                  onClick={() => startRename({ name: getName(selectedFile.path), path: selectedFile.path, isFolder: false, children: [] })}
                >
                  Rename
                </Button>
                <Button
                  size="sm" variant="outline" className="text-destructive hover:bg-destructive/10"
                  onClick={() => startDelete({ name: getName(selectedFile.path), path: selectedFile.path, isFolder: false, children: [] })}
                >
                  Delete
                </Button>
                <Button size="sm" onClick={save} disabled={isSaving}>
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
            <Textarea
              value={editorContent}
              onChange={e => setEditorContent(e.target.value)}
              className="flex-1 resize-none rounded-none border-0 font-mono text-sm p-4 focus-visible:ring-0"
            />
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <FileText className="w-10 h-10 opacity-20" />
            <span className="text-sm">Select a document to edit</span>
          </div>
        )}
      </div>

      {/* Move dialog */}
      {moveTarget && currentVault && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setMoveTarget(null)}
        >
          <div
            className="bg-background border rounded-xl shadow-xl p-5 w-80 space-y-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">Move "{getName(moveTarget.path)}"</span>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setMoveTarget(null)}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Choose destination folder:</p>
            <div className="max-h-56 overflow-auto space-y-1 border rounded-lg p-1">
              {allFolders.map(folder => (
                <button
                  key={folder}
                  onClick={() => moveDocument(moveTarget, folder)}
                  className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent flex items-center gap-2"
                >
                  <Folder className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{folder}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setMoveTarget(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: delete / rename / create folder / create note */}
      {modal.type !== 'none' && (() => {
        const isInput = modal.type !== 'delete';
        const inputValue = isInput ? (modal as { value: string }).value : '';

        let title = '';
        let placeholder = '';
        let confirmLabel = '';
        let onConfirm: () => void = () => {};

        if (modal.type === 'delete') {
          title = `Delete "${modal.node.name || getName(modal.node.path)}"?`;
          confirmLabel = 'Delete';
          onConfirm = () => confirmDelete(modal.node);
        } else if (modal.type === 'rename') {
          title = `Rename "${modal.node.name}"`;
          placeholder = 'New name';
          confirmLabel = 'Rename';
          onConfirm = () => confirmRename(modal.node, modal.value);
        } else if (modal.type === 'createFolder') {
          title = 'New folder';
          placeholder = 'Folder name';
          confirmLabel = 'Create';
          onConfirm = () => confirmCreateFolder(modal.value);
        } else if (modal.type === 'createNote') {
          title = 'New note';
          placeholder = 'Note name';
          confirmLabel = 'Create';
          onConfirm = () => confirmCreateNote(modal.value);
        }

        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setModal({ type: 'none' })}
          >
            <div
              className="bg-background border rounded-xl shadow-xl p-5 w-80 space-y-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{title}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setModal({ type: 'none' })}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>

              {modal.type === 'delete' && (
                <p className="text-sm text-muted-foreground">
                  This will permanently remove{' '}
                  <strong>{modal.node.isFolder ? 'the folder and all its contents' : 'this document'}</strong>
                  {' '}from your vault. This action cannot be undone.
                </p>
              )}

              {isInput && (
                <Input
                  autoFocus
                  value={inputValue}
                  placeholder={placeholder}
                  onChange={e => {
                    if (modal.type === 'rename')
                      setModal({ ...modal, value: e.target.value });
                    else if (modal.type === 'createFolder')
                      setModal({ ...modal, value: e.target.value });
                    else if (modal.type === 'createNote')
                      setModal({ ...modal, value: e.target.value });
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') onConfirm();
                    if (e.key === 'Escape') setModal({ type: 'none' });
                  }}
                />
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setModal({ type: 'none' })}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant={modal.type === 'delete' ? 'destructive' : 'default'}
                  onClick={onConfirm}
                  disabled={isInput && !inputValue.trim()}
                >
                  {modal.type === 'delete' ? (
                    <><Trash2 className="w-3.5 h-3.5 mr-1" />{confirmLabel}</>
                  ) : (
                    <><Check className="w-3.5 h-3.5 mr-1" />{confirmLabel}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
